import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import HeadActionButtons, { IHeadButton } from './ContentsHeadButtons';
import {
  CompanyIcon,
  HeaderRow,
  OrganizationName,
  SurveyName,
  SurveyOrganizationCont,
  TitleBlock,
} from './CreateSurveyHeader.styles';
import { CreateSurveyCardsNames } from '../../constans/state';
import { useSurveyTable } from '../../hooks';
import { useAuth } from '../../hooks/useAuth';
import { PageMetaItem } from '../../styles/globals';
import EyeIcon from '@/assets/icons/eyeIconWhite.svg?react';
import SaveIcon from '@/assets/icons/saveIcon.svg?react';
import SendIcon from '@/assets/icons/send.svg?react';

interface ICreateSurveyHeader {
  surveyName: string;
  organizationName: string;
  scroll: boolean;
  loading: boolean;
  onSubmitContents: () => Promise<void>;
  onShareResults: () => Promise<void>;
  preview_link: string;
  isDisabled: boolean;
}

const CreateSurveyHeader: FC<ICreateSurveyHeader> = ({
  surveyName,
  organizationName,
  scroll,
  loading,
  onSubmitContents,
  onShareResults,
  preview_link,
  isDisabled,
}) => {
  const { t } = useTranslation();
  const { getAppliedFilters } = useSurveyTable();
  const { card } = getAppliedFilters();
  const { isEBHRMember } = useAuth();

  const buttons = useMemo<IHeadButton[]>(() => {
    switch (card) {
      case CreateSurveyCardsNames.shareResults:
        return [
          {
            id: 'results-share',
            callToAction: async () => await onShareResults(),
            name: t('viewSurvey.topBar.share'),
            icon: <SendIcon />,
            loading,
            disabled: isDisabled,
          },
        ];
      case CreateSurveyCardsNames.contents:
      case CreateSurveyCardsNames.schedule:
        return [
          ...(card === CreateSurveyCardsNames.contents
            ? [
              {
                id: 'contents-preview',
                callToAction: () => {
                  window.open(preview_link, '_blank');
                },
                name: t('viewSurvey.topBar.previewTheSurvey'),
                icon: <EyeIcon />,
                loading,
                variant: 'secondary' as const,
              },
            ]
            : []),
          {
            id: 'contents-save',
            callToAction: async () => await onSubmitContents(),
            name: t('viewSurvey.topBar.save'),
            icon: <SaveIcon />,
            loading,
          },
        ];
      default:
        return [];
    }
  }, [card, loading, isDisabled, preview_link, onSubmitContents, onShareResults, t]);

  return (
    <HeaderRow id="SurveyNameCont">
      <TitleBlock>
        <SurveyName id="SurveyName" data-testid="surveyName" $scroll={scroll} title={surveyName}>
          {surveyName}
        </SurveyName>
        {isEBHRMember ? (
          <SurveyOrganizationCont id="SurveyOrganizationCont" $scroll={scroll}>
            <PageMetaItem>
              <CompanyIcon id="CompanyIcon" />
              {t('viewSurvey.topBar.organisation')}:
              <OrganizationName id="OrganizationName" data-testid="surveyOrganizationName">
                {organizationName}
              </OrganizationName>
            </PageMetaItem>
          </SurveyOrganizationCont>
        ) : null}
      </TitleBlock>
      {buttons.length > 0 && <HeadActionButtons scroll={scroll} buttons={buttons} />}
    </HeaderRow>
  );
};

export default CreateSurveyHeader;
